/**
 * Запрашивает разрешение на Browser Notification у пользователя.
 * Нужен жест пользователя (клик): без него мобильные браузеры часто не показывают диалог ОС.
 * @returns текущий или обновлённый статус разрешения
 */
export const requestNotificationPermission =
  async (): Promise<NotificationPermission> => {
    if (typeof Notification === 'undefined') {
      return 'denied'
    }

    if (Notification.permission === 'granted') {
      return 'granted'
    }

    if (Notification.permission === 'denied') {
      return 'denied'
    }

    // Совместимость: старые Safari отдают callback, современные — Promise
    return new Promise(resolve => {
      let isSettled = false
      const settle = (permission: NotificationPermission): void => {
        if (isSettled) {
          return
        }
        isSettled = true
        resolve(permission)
      }

      try {
        const permissionResult = Notification.requestPermission(settle)
        if (
          permissionResult != null &&
          typeof (permissionResult as Promise<NotificationPermission>).then ===
            'function'
        ) {
          void (permissionResult as Promise<NotificationPermission>).then(
            settle,
            () => settle(Notification.permission)
          )
        }
      } catch (_) {
        settle(Notification.permission)
      }
    })
  }

/**
 * Показывает системное уведомление о новом заказе (только при granted).
 * @param params.orderId — номер заказа
 * @param params.totalPrice — сумма заказа в рублях
 * @param params.onClick — колбэк при клике по уведомлению
 */
export const showNewOrderNotification = (params: {
  orderId: number
  totalPrice: number
  onClick?: () => void
}): void => {
  if (typeof Notification === 'undefined') {
    return
  }

  if (Notification.permission !== 'granted') {
    return
  }

  const { orderId, totalPrice, onClick } = params

  // Сумму не показываем, если она неизвестна или некорректна (poll без цены)
  const notificationBody =
    totalPrice > 0 ? `Сумма ${totalPrice} ₽` : undefined

  const notification = new Notification(`Новый заказ №${orderId}`, {
    ...(notificationBody != null ? { body: notificationBody } : {}),
    tag: `order-${orderId}`,
  })

  notification.onclick = () => {
    window.focus()
    onClick?.()
    notification.close()
  }
}
